import { Component, OnInit, Input, Output, EventEmitter, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { saveAs as fileSaverSaveAs } from 'file-saver';
import { PHBHPLayoutPackageService } from 'ph-bhp-feature-packages/ph-bhp-layout-package/public_api';
import { TranslationKeys } from 'ph-bhp-localization';
import { PHBHPFeaturePackageEvaluationService } from '../ph-bhp-feature-package-evaluation.service';
import { PhBhpPdfSopReportComponent } from '../ph-bhp-pdf-sop-report/ph-bhp-pdf-sop-report.component';

const dateFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' } as Intl.DateTimeFormatOptions;

@Component({
  selector: 'lib-php-bhp-summarized-report-excel-export',
  templateUrl: './php-bhp-summarized-report-excel-export.component.html',
  styleUrls: ['./php-bhp-summarized-report-excel-export.component.css']
})
export class PhpBhpSummarizedReportExcelExportComponent implements OnInit, OnChanges {
  private static readonly TITLE_INITIAL_FIELD = [
    { 'field': 'title', 'title': 'EVALUATION.TITLE', 'group': '' }
  ];
  private static readonly FREQUENCY_DISTRIBUTION_INITIAL_FIELDS = [
    { 'field': 'stepName', 'title': 'EVALUATION.STEP_NAME', 'group': '' }
  ];
  private static readonly OBSERVE_INITIAL_FIELDS = [
    { 'field': 'name', 'title': 'CONFIGURE.DESCRIPTION', 'group': '' }
  ];
  @Input() processCompliance;
  @Input() frequencyDistribution;
  @Input() subStepProcessCompliance;
  @Input() sopPredefinedComments;

  @Input() overallCompliance;
  @Input() fiveMoments;
  @Input() asepticStatistics;
  @Input() observeComments;

  @Input() exportComponent: PhBhpPdfSopReportComponent;

  @Input() clickExcelBtn: boolean = false;
  @Input() userData;
  @Output() childData = new EventEmitter();
  @Output() resetClickBtn = new EventEmitter<'image' | 'excel' | 'pdf'>();
  @ViewChild('excelexport') excelexport;
  font = "8px OpenSans-Regular"
  headerTitle: string;
  appName: string = 'Hartmann Hygiene Platform';
  reportName: string;
  wards: { wardId: number, wardName: string }[] = [];
  departments: { departmentId: number, departmentName: string }[] = [];
  professionalCategories: { categoryId: number, categoryName: string }[] = [];
  professionalSubCategories: { subCategoryId: number, subCategoryName: string }[] = [];
  institutions: { institutionId: number, institutionName: string }[] = [];
  showProfessionalSubCategory: boolean = false;
  translationKeys: typeof TranslationKeys = TranslationKeys;

  constructor(private layoutService: PHBHPLayoutPackageService, private evaluationService: PHBHPFeaturePackageEvaluationService) {
    this.headerTitle = 'Hartmann Hygiene Platform | ' + this.layoutService.translateText('EVALUATION.SUMMARISED_REPORT_OK');
    this.reportName = this.layoutService.translateText('EVALUATION.SUMMARISED_REPORT_OK');
  }

  ngOnInit(): void {

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.clickExcelBtn && changes.clickExcelBtn.currentValue) {
      this.excelDownload(this.excelexport);
      this.resetClickBtn.emit('excel');
    }
  }

  async excelDownload(component) {
    this.layoutService.showLoader = true;
    let processComplianceCharts = [];
    let frequencyDistributionCharts = [];
    let subStepProcessComplianceCharts = [];
    let predefinedCommentsCharts = [];
    let overallComplianceCharts = [];
    let fiveMomentsCharts = [];
    let asepticStatisticsCharts = [];
    let observeCommentsCharts = [];

    let processComplianceChartsStartIndex = 0;
    let frequencyDistributionChartsStartIndex = 0;
    let subStepProcessComplianceChartsStartIndex = 0;
    let predefinedCommentsChartsStartIndex = 0;
    let overallComplianceChartsStartIndex = 0;
    let fiveMomentsChartsStartIndex = 0;
    let asepticStatisticsChartsStartIndex = 0;
    let observeCommentsChartsStartIndex = 0;

    let processComplianceFilters = this.processCompliance && this.processCompliance.length > 0 ? this.processCompliance[0].sopFilters[0].filter ?? [] : [];
    let processComplianceSopIds = this.processCompliance && this.processCompliance.length > 0 ? this.processCompliance[0].sopFilters[0].sopiDs.split(",").map(Number) ?? [] : [];

    let frequencyDistributionFilters = this.frequencyDistribution && this.frequencyDistribution.length > 0 ? this.frequencyDistribution[0].sopFilters[0].filter ?? [] : [];
    let frequencyDistributionSopIds = this.frequencyDistribution && this.frequencyDistribution.length > 0 ? this.frequencyDistribution[0].sopFilters[0].sopiDs.split(",").map(Number) ?? [] : [];

    let predefinedCommentsFilters = this.sopPredefinedComments && this.sopPredefinedComments.length > 0 ? this.sopPredefinedComments[0].commentFilters[0].filters ?? [] : [];
    let predefinedCommentsSopIds = this.sopPredefinedComments && this.sopPredefinedComments.length > 0 ? this.sopPredefinedComments[0].commentFilters[0].sopiDs.split(",").map(Number) ?? [] : [];

    let subStepProcessComplianceFilters = this.subStepProcessCompliance && this.subStepProcessCompliance.length > 0 ? this.subStepProcessCompliance[0]?.sopFilters[0]?.filter ?? [] : [];
    let subStepProcessComplianceSopIds = this.subStepProcessCompliance && this.subStepProcessCompliance.length > 0 ? this.subStepProcessCompliance[0]?.sopFilters[0]?.sopiDs.split(",").map(Number) ?? [] : [];

    let overallComplianceFilters = this.overallCompliance?.overAllComplianceFilter?.filter ?? [];
    let fiveMomentsFilters = this.fiveMoments?.fiveMomentFilter?.filter ?? [];
    let asepticStatisticsFilters = this.asepticStatistics?.asepticFilter?.filter ?? [];
    let observeCommentsFilters = this.observeComments?.commentFilters?.filter ?? [];

    let totalFilters = [
      ...processComplianceFilters,
      ...frequencyDistributionFilters,
      ...subStepProcessComplianceFilters,
      ...predefinedCommentsFilters,
      ...overallComplianceFilters,
      ...fiveMomentsFilters,
      ...asepticStatisticsFilters,
      ...observeCommentsFilters
    ];

    let countryIds = Array.from(new Set(totalFilters.map(filter => filter.countryID as number).filter(id => id)));
    let institutionIds = Array.from(new Set(totalFilters.map(filter => filter.instutionIDs as number).filter(id => id)));
    let wardIds = Array.from(new Set(totalFilters.map(filter => filter.wardID as number).filter(id => id)));
    let departmentIds = Array.from(new Set(totalFilters.map(filter => filter.departmentID as number).filter(id => id)));
    let professionalCategoryIds = Array.from(new Set(totalFilters.map(filter => filter.professionalCategoryID as number).filter(id => id)));
    let professionalSubCategoryIds = Array.from(new Set(totalFilters.map(filter => filter.subprofessionalCategoryID as number).filter(id => id)));

    let sopIds = Array.from(new Set([...processComplianceSopIds, ...frequencyDistributionSopIds, ...subStepProcessComplianceSopIds, ...predefinedCommentsSopIds]));

    let filtersData = {
      countryIds,
      institutionIds,
      wardIds,
      departmentIds,
      professionalCategoryIds,
      professionalSubCategoryIds,
      sopIds,
      userId: this.userData.roleName.roleId > 2 ? this.userData.userId : null
    };

    let filtersDataResponse = await this.evaluationService.getFiltersData(filtersData).toPromise();

    this.showProfessionalSubCategory = this.userData.roleName.roleId > 2 ? filtersDataResponse.showProfessionalSubCategory : true;

    let processComplianceFilterData = this.processFilterData(processComplianceFilters, filtersDataResponse);
    let processComplianceSopData = this.processSopData(processComplianceSopIds, filtersDataResponse);

    let frequencyDistributionFilterData = this.processFilterData(frequencyDistributionFilters, filtersDataResponse);
    let frequencyDistributionSopData = this.processSopData(frequencyDistributionSopIds, filtersDataResponse);

    let subStepProcessComplianceFilterData = this.processFilterData(subStepProcessComplianceFilters, filtersDataResponse);
    let subStepProcessComplianceSopData = this.processSopData(subStepProcessComplianceSopIds, filtersDataResponse);

    let predefinedCommentsFilterData = this.processFilterData(predefinedCommentsFilters, filtersDataResponse);
    let predefinedCommentsSopData = this.processSopData(predefinedCommentsSopIds, filtersDataResponse);

    let overallComplianceFilterData = this.processFilterData(overallComplianceFilters, filtersDataResponse);
    let fiveMomentsFilterData = this.processFilterData(fiveMomentsFilters, filtersDataResponse);
    let asepticStatisticsFilterData = this.processFilterData(asepticStatisticsFilters, filtersDataResponse);
    let observeCommentsFilterData = this.processFilterData(observeCommentsFilters, filtersDataResponse);

    const options = component.workbookOptions();
    const kendoRows = options.sheets[0].rows;
    let addedIndex = 0;

    addedIndex = this.addEmptyRow(kendoRows, addedIndex);;

    kendoRows.splice(addedIndex, 0, {
      cells: [{
        value: this.layoutService.translateText(this.reportName),
        colSpan: 7,
        bold: true,
        fontSize: 16,
        borderBottom: { color: 'black', size: 1 },
      }
      ],
      type: "custom",
      height: 50
    })
    addedIndex++;

    let roleName = this.userData?.roleName?.roleName || '';
    let userRoleArray = [{
      value: this.layoutService.translateText('USER_MANAGEMENT.USER_ROLE'),
      colSpan: 1,
      bold: true,
    },
    {
      value: roleName,
      colSpan: 1
    },
    {
      colSpan: 1
    },
    {
      colSpan: 1
    },
    {
      colSpan: 1
    },
    {
      value: this.layoutService.translateText('EVALUATION.CREATION_DATE'),
      colSpan: 1,
      bold: true,
    },
    {
      value: new Date().toLocaleDateString('en-GB', dateFormatOptions),
      colSpan: 1
    }];

    kendoRows.splice(addedIndex, 0, {
      cells: userRoleArray,
      type: "custom",
    });
    addedIndex++;

    if (this.processCompliance && this.processCompliance.length > 0 && this.processCompliance[0].processComplianceMainList && this.processCompliance[0].processComplianceMainList.length > 0 && processComplianceFilterData.length > 0) {
      addedIndex = this.addHeaderRow(kendoRows, 'DASHBOARD.PROCESS_COMPLIANCE', addedIndex, false);
      addedIndex = this.addSopDataRows(kendoRows, processComplianceSopData, addedIndex);
      addedIndex = await this.addFiltersDataRows(kendoRows, processComplianceFilterData, addedIndex,);

      let processComplianceGridData = [];
      let processComplianceFields = [...PhpBhpSummarizedReportExcelExportComponent.TITLE_INITIAL_FIELD];

      processComplianceGridData = this.processCompliance[0].processComplianceMainList[0].processComplianceList
        .map(e => {
          return { ...e, title: this.layoutService.translateText(e.title) }
        });

      let nonBenchmarkFilters = this.processCompliance[0].processComplianceMainList.filter(filter => !filter.isBenchMark);
      nonBenchmarkFilters.forEach((filter, index) => {
        let res = filter.processComplianceList;
        processComplianceFields.push(
          { 'field': `totalCompliantObservationsforfilter${index + 1}`, 'title': 'EVALUATION.AMOUNT_COMPLIANT_OBSERVATIONS', 'group': `EVALUATION.FILTER${index + 1}` },
          { 'field': `totalObservationsforfilter${index + 1}`, 'title': 'EVALUATION.TOTAL_AMOUNT_OBSERVATIONS', 'group': `EVALUATION.FILTER${index + 1}` },
        );
        for (let i = 0; i < res.length; i++) {
          if (processComplianceGridData[i]) {
            processComplianceGridData[i][`totalObservationsforfilter${index + 1}`] = res[i]['totalObservations'];
            processComplianceGridData[i][`totalCompliantObservationsforfilter${index + 1}`] = res[i]['totalCompliantObservations'];
          }
        }
      });

      let processComplianceFieldsGroupIndices = this.getGroupIndices(processComplianceFields);
      addedIndex = this.addGroupHeaderRows(kendoRows, processComplianceFieldsGroupIndices, processComplianceFields, addedIndex);
      addedIndex = this.addHeaderAndDataRows(kendoRows, processComplianceFields, processComplianceGridData, addedIndex);

      processComplianceCharts = this.getKendoCharts('processCompliance');
      if (processComplianceCharts && processComplianceCharts.length > 0) {
        addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.DIAGRAM', addedIndex, false);
        processComplianceChartsStartIndex = addedIndex;
        addedIndex = this.addPlaceholderRowsForCharts(processComplianceCharts, addedIndex, kendoRows);
      }
    }

    if (this.frequencyDistribution && this.frequencyDistribution.length > 0 && this.frequencyDistribution[0].subStepProcessComplianceMainList && this.frequencyDistribution[0].subStepProcessComplianceMainList.length > 0 && frequencyDistributionFilterData.length > 0) {
      addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.FREQUENCY_DISTRIBUTION.DISPLAY_TEXT', addedIndex, true);
      addedIndex = this.addSopDataRows(kendoRows, frequencyDistributionSopData, addedIndex);
      addedIndex = await this.addFiltersDataRows(kendoRows, frequencyDistributionFilterData, addedIndex,);

      let frequencyDistributionGridData = this.frequencyDistribution[0].subStepProcessComplianceMainList[0].subStepProcessComplianceList;
      let frequencyDistributionFields = [...PhpBhpSummarizedReportExcelExportComponent.FREQUENCY_DISTRIBUTION_INITIAL_FIELDS];

      let nonBenchmarkFilters = this.frequencyDistribution[0].subStepProcessComplianceMainList.filter(filter => !filter.isBenchMark);
      nonBenchmarkFilters.forEach((filter, index) => {
        let res = filter.subStepProcessComplianceList;
        frequencyDistributionFields.push(
          { 'field': `totalCompliantObservationsforfilter${index + 1}`, 'title': 'EVALUATION.AMOUNT_COMPLIANT_OBSERVATIONS', 'group': `EVALUATION.FILTER${index + 1}` },
          { 'field': `totalObservationsforfilter${index + 1}`, 'title': 'EVALUATION.TOTAL_AMOUNT_OBSERVATIONS', 'group': `EVALUATION.FILTER${index + 1}` },
        );
        for (let i = 0; i < res.length; i++) {
          if (frequencyDistributionGridData[i]) {
            frequencyDistributionGridData[i][`totalObservationsforfilter${index + 1}`] = res[i]['totalObservations'];
            frequencyDistributionGridData[i][`totalCompliantObservationsforfilter${index + 1}`] = res[i]['totalCompliantObservations'];
          }
        }
      });

      let frequencyDistributionFieldsGroupIndices = this.getGroupIndices(frequencyDistributionFields);
      addedIndex = this.addGroupHeaderRows(kendoRows, frequencyDistributionFieldsGroupIndices, frequencyDistributionFields, addedIndex);
      addedIndex = this.addHeaderAndDataRows(kendoRows, frequencyDistributionFields, frequencyDistributionGridData, addedIndex);

      frequencyDistributionCharts = this.getKendoCharts('frequencyDistribution');
      if (frequencyDistributionCharts && frequencyDistributionCharts.length > 0) {
        addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.DIAGRAM', addedIndex, false);
        frequencyDistributionChartsStartIndex = addedIndex;
        addedIndex = this.addPlaceholderRowsForCharts(frequencyDistributionCharts, addedIndex, kendoRows);
      }
    }

    if (this.subStepProcessCompliance && this.subStepProcessCompliance.length > 0 && this.subStepProcessCompliance[0].subStepProcessComplianceMainList && this.subStepProcessCompliance[0].subStepProcessComplianceMainList.length > 0 && subStepProcessComplianceFilterData.length > 0) {
      addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.SUB_STEP_COMPLIANCE', addedIndex, true);
      addedIndex = this.addSopDataRows(kendoRows, subStepProcessComplianceSopData, addedIndex);
      addedIndex = await this.addFiltersDataRows(kendoRows, subStepProcessComplianceFilterData, addedIndex,);

      let subStepProcessComplianceGridData = this.subStepProcessCompliance[0].subStepProcessComplianceMainList[0].subStepProcessComplianceList;
      let subStepProcessComplianceFields = [...PhpBhpSummarizedReportExcelExportComponent.FREQUENCY_DISTRIBUTION_INITIAL_FIELDS];

      let nonBenchmarkFilters = this.subStepProcessCompliance[0].subStepProcessComplianceMainList.filter(filter => !filter.isBenchMark);
      nonBenchmarkFilters.forEach((filter, index) => {
        let res = filter.subStepProcessComplianceList;
        subStepProcessComplianceFields.push(
          { 'field': `totalCompliantObservationsforfilter${index + 1}`, 'title': 'EVALUATION.AMOUNT_COMPLIANT_OBSERVATIONS', 'group': `EVALUATION.FILTER${index + 1}` },
          { 'field': `totalObservationsforfilter${index + 1}`, 'title': 'EVALUATION.TOTAL_AMOUNT_OBSERVATIONS', 'group': `EVALUATION.FILTER${index + 1}` },
        );
        for (let i = 0; i < res.length; i++) {
          if (subStepProcessComplianceGridData[i]) {
            subStepProcessComplianceGridData[i][`totalObservationsforfilter${index + 1}`] = res[i]['totalObservations'];
            subStepProcessComplianceGridData[i][`totalCompliantObservationsforfilter${index + 1}`] = res[i]['totalCompliantObservations'];
          }
        }
      });

      let subStepProcessComplianceFieldsGroupIndices = this.getGroupIndices(subStepProcessComplianceFields);
      addedIndex = this.addGroupHeaderRows(kendoRows, subStepProcessComplianceFieldsGroupIndices, subStepProcessComplianceFields, addedIndex);
      addedIndex = this.addHeaderAndDataRows(kendoRows, subStepProcessComplianceFields, subStepProcessComplianceGridData, addedIndex);

      subStepProcessComplianceCharts = this.getKendoCharts('subStepProcessCompliance');
      if (subStepProcessComplianceCharts && subStepProcessComplianceCharts.length > 0) {
        addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.DIAGRAM', addedIndex, false);
        subStepProcessComplianceChartsStartIndex = addedIndex;
        addedIndex = this.addPlaceholderRowsForCharts(subStepProcessComplianceCharts, addedIndex, kendoRows);
      }
    }

    if (this.sopPredefinedComments && this.sopPredefinedComments.length > 0 && this.sopPredefinedComments[0].sopObservationPredefinedCommentsMainList && this.sopPredefinedComments[0].sopObservationPredefinedCommentsMainList.length > 0 && predefinedCommentsFilterData.length > 0) {
      addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.SOP_COMMENTS', addedIndex, true);
      addedIndex = this.addSopDataRows(kendoRows, predefinedCommentsSopData, addedIndex);
      addedIndex = await this.addFiltersDataRows(kendoRows, predefinedCommentsFilterData, addedIndex,);

      let predefinedCommentsGridData = []
      let predefinedCommentsFields = [...PhpBhpSummarizedReportExcelExportComponent.TITLE_INITIAL_FIELD];

      for (let i = 0; i < this.sopPredefinedComments[0].sopObservationPredefinedCommentsMainList.length; i++) {
        const predefinedCommentsList = this.sopPredefinedComments[0].sopObservationPredefinedCommentsMainList[i].sopObservationPredefinedCommentsList;
        for (let j = 0; j < predefinedCommentsList.length; j++) {
          const predefinedComment = predefinedCommentsList[j];

          if (predefinedComment.predefinedCommentID == 1) {
            this.sopPredefinedComments[0].sopObservationPredefinedCommentsMainList[i].sopObservationPredefinedCommentsList[j].commentName = this.layoutService.translateText('SOP_OBSERVATION.JEWELLERY_OR_WRIST_WATCH');
          }
          if (predefinedComment.predefinedCommentID == 2) {
            this.sopPredefinedComments[0].sopObservationPredefinedCommentsMainList[i].sopObservationPredefinedCommentsList[j].commentName = this.layoutService.translateText('SOP_OBSERVATION.ARTIFICIAL_NAILS_OR_VARNISH');
          }
          if (predefinedComment.predefinedCommentID == 3) {
            this.sopPredefinedComments[0].sopObservationPredefinedCommentsMainList[i].sopObservationPredefinedCommentsList[j].commentName = this.layoutService.translateText('SOP_OBSERVATION.HAND_DISINFECTION_WRONG_EXECUTED');
          }
          if (predefinedComment.predefinedCommentID == 4) {
            this.sopPredefinedComments[0].sopObservationPredefinedCommentsMainList[i].sopObservationPredefinedCommentsList[j].commentName = this.layoutService.translateText('SOP_OBSERVATION.SKIN_ANTISEPSIS_WRONG_EXECUTED');
          }
          if (predefinedComment.predefinedCommentID == 5) {
            this.sopPredefinedComments[0].sopObservationPredefinedCommentsMainList[i].sopObservationPredefinedCommentsList[j].commentName = this.layoutService.translateText('SOP_OBSERVATION.PROCESS_INTERRUPTED');
          }
        }
      }

      predefinedCommentsGridData = this.sopPredefinedComments[0].sopObservationPredefinedCommentsMainList[0].sopObservationPredefinedCommentsList;
      predefinedCommentsGridData.forEach((value, index) => {
        if (predefinedCommentsGridData[index].commentName)
          predefinedCommentsGridData[index].commentName = this.layoutService.translateText(value.commentName);
      });

      let nonBenchmarkFilters = this.sopPredefinedComments[0].sopObservationPredefinedCommentsMainList.filter(filter => !filter.isBenchMark);
      nonBenchmarkFilters.forEach((filter, index) => {
        let res = filter.sopObservationPredefinedCommentsList;
        predefinedCommentsFields.push(
          { 'field': `totalTouchedComments${index + 1}`, 'title': this.layoutService.translateText('EVALUATION.TOTAL.NUMBER_TOUCHED_COMMENTS'), 'group': `EVALUATION.FILTER${index + 1}` },
          { 'field': `totalObservations${index + 1}`, 'title': this.layoutService.translateText('EVALUATION.TOTAL.AMOUNT_OBSERVATION'), 'group': `EVALUATION.FILTER${index + 1}` },
          { 'field': `predefinedCommentsPercentage${index + 1}`, 'title': this.layoutService.translateText('EVALUATION.TOUCHED_COMMENTS_PERCENT'), 'group': `EVALUATION.FILTER${index + 1}` },
        );
        for (let i = 0; i < res.length; i++) {
          if (predefinedCommentsGridData[i]) {
            predefinedCommentsGridData[i]['title'] = this.layoutService.translateText(res[i]['commentName']);
            predefinedCommentsGridData[i][`totalTouchedComments${index + 1}`] = res[i]['totalTouchedComments'];
            predefinedCommentsGridData[i][`totalObservations${index + 1}`] = res[i]['totalObservations'];
            predefinedCommentsGridData[i][`predefinedCommentsPercentage${index + 1}`] = res[i]['predefinedCommentsPercentage'];
          }
        }
      });

      this.sopPredefinedComments[0].sopObservationFreeTextCommentsList.forEach(comment => {
        if (comment.commentName.length > 50) {
          comment.commentName = comment.commentName.substring(0, 47) + '...';
        }
      });

      let freeTextCommentsGridData = this.sopPredefinedComments[0].sopObservationFreeTextCommentsList;
      freeTextCommentsGridData.forEach((value, index) => {
        if (freeTextCommentsGridData[index].title)
          freeTextCommentsGridData[index].title = this.layoutService.translateText(value.title);
      });

      let freeTextCommentsFields = [
        { 'field': 'title', 'title': 'EVALUATION.SOP_TITLE' },
        { 'field': 'commentName', 'title': 'EVALUATION.FREE_TEXT_COMMENTS' }
      ];

      let predefinedCommentsFieldsGroupIndices = this.getGroupIndices(predefinedCommentsFields);
      addedIndex = this.addGroupHeaderRows(kendoRows, predefinedCommentsFieldsGroupIndices, predefinedCommentsFields, addedIndex);
      addedIndex = this.addHeaderAndDataRows(kendoRows, predefinedCommentsFields, predefinedCommentsGridData, addedIndex);
      addedIndex = this.addEmptyRow(kendoRows, addedIndex);

      if (freeTextCommentsGridData && freeTextCommentsGridData.length > 0) {
        const groupedByFilterNumber = freeTextCommentsGridData.reduce((acc, item) => {
          if (!acc[item.filterNumber]) {
            acc[item.filterNumber] = [];
          }
          acc[item.filterNumber].push(item);
          return acc;
        }, {});

        const groupedFreeTextComments = Object.values(groupedByFilterNumber);


        groupedFreeTextComments.forEach((commentsArray, filterIndex) => {
          if ((commentsArray as any[]).length > 0) {
            kendoRows.splice(addedIndex++, 0, {
              cells: [{ value: "", colSpan: freeTextCommentsFields.length, bold: true }],
              type: "custom"
            });
            kendoRows.splice(addedIndex++, 0, {
              cells: [{ value: this.layoutService.translateText(`EVALUATION.FILTER${filterIndex + 1}`) + ': ' + this.layoutService.translateText('SOP_MANAGEMENT.FREECOMMENTS'), colSpan: predefinedCommentsFields.length, bold: true }],
              type: "custom"
            });

            const commentsHeaderCells = freeTextCommentsFields.map(field => {
              return { value: this.layoutService.translateText(field.title), colSpan: 1, bold: true };
            });

            kendoRows.splice(addedIndex, 0, {
              cells: commentsHeaderCells,
              type: "header"
            });
            addedIndex++;

            (commentsArray as any[]).forEach(comment => {
              const commentCells = freeTextCommentsFields.map(field => {
                return { value: comment[field.field] || "", colSpan: 1, bold: false };
              });

              while (commentCells.length < predefinedCommentsFields.length) {
                commentCells.push({ value: "", colSpan: 1, bold: false });
              }

              kendoRows.splice(addedIndex++, 0, {
                cells: commentCells,
                type: "data"
              });
            });
          }
        });
      }

      predefinedCommentsCharts = this.getKendoCharts('predefinedComments');
      if (predefinedCommentsCharts && predefinedCommentsCharts.length > 0) {
        addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.DIAGRAM', addedIndex, false);
        predefinedCommentsChartsStartIndex = addedIndex;
        addedIndex = this.addPlaceholderRowsForCharts(predefinedCommentsCharts, addedIndex, kendoRows);
      }
    }

    if (this.overallCompliance && this.overallCompliance.observeEvaluationOverAllComplianceResultList && this.overallCompliance.observeEvaluationOverAllComplianceResultList.length > 0 && overallComplianceFilterData.length > 0) {
      addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.OVERALL_COMPLIANCE', addedIndex, true);
      addedIndex = await this.addFiltersDataRows(kendoRows, overallComplianceFilterData, addedIndex,);

      let overallComplianceGridData = this.overallCompliance.overAllComplianceTable;
      let overallComplianceFields = [...PhpBhpSummarizedReportExcelExportComponent.OBSERVE_INITIAL_FIELDS];
      let overallComplianceBenchmarkFields = [...PhpBhpSummarizedReportExcelExportComponent.OBSERVE_INITIAL_FIELDS];
      let isBenchmark = false;

      this.translateGridData(overallComplianceGridData);

      if (this.overallCompliance.observeEvaluationOverAllComplianceResultList) {
        let nonBenchmarkFilters = this.overallCompliance.observeEvaluationOverAllComplianceResultList.filter(filter => !filter.isBenchMark);
        nonBenchmarkFilters.forEach((filter, index) => {
          overallComplianceFields.push({ 'field': `filter${index + 1}Value`, 'title': `EVALUATION.FILTER${index + 1}`, 'group': '' });
        });
        let benchmarkFilters = this.overallCompliance.observeEvaluationOverAllComplianceResultList.filter(filter => filter.isBenchMark);
        if (benchmarkFilters.length > 0) {
          isBenchmark = true;
          benchmarkFilters.forEach((filter, index) => {
            overallComplianceBenchmarkFields.push({ 'field': `filter${index + 1}BenchmarkValue`, 'title': `EVALUATION.BENCHMARK${index + 1}`, 'group': '' });
          });
        }
      }

      let overallComplianceFieldsGroupIndices = this.getGroupIndices(overallComplianceFields);
      addedIndex = this.addGroupHeaderRows(kendoRows, overallComplianceFieldsGroupIndices, overallComplianceFields, addedIndex);
      addedIndex = this.addHeaderAndDataRows(kendoRows, overallComplianceFields, overallComplianceGridData, addedIndex);

      if (isBenchmark) {
        let overallComplianceBenchmarkFieldsGroupIndices = this.getGroupIndices(overallComplianceBenchmarkFields);
        addedIndex = this.addEmptyRow(kendoRows, addedIndex);
        addedIndex = this.addGroupHeaderRows(kendoRows, overallComplianceBenchmarkFieldsGroupIndices, overallComplianceBenchmarkFields, addedIndex);
        addedIndex = this.addHeaderAndDataRows(kendoRows, overallComplianceBenchmarkFields, overallComplianceGridData, addedIndex);
      }

      overallComplianceCharts = this.getKendoCharts('overallCompliance');
      if (overallComplianceCharts && overallComplianceCharts.length > 0) {
        addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.DIAGRAM', addedIndex, false);
        overallComplianceChartsStartIndex = addedIndex;
        addedIndex = this.addPlaceholderRowsForCharts(overallComplianceCharts, addedIndex, kendoRows);
      }
    }

    if (this.fiveMoments && this.fiveMoments.observeEvaluationFiveMomentResultList && this.fiveMoments.observeEvaluationFiveMomentResultList.length > 0 && fiveMomentsFilterData.length > 0) {
      addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.FIVE_MOMENTS', addedIndex, true);
      addedIndex = await this.addFiltersDataRows(kendoRows, fiveMomentsFilterData, addedIndex,);

      let fiveMomentsGridData = this.fiveMoments.observationFiveMomentTables;
      let fiveMomentsFields = [...PhpBhpSummarizedReportExcelExportComponent.OBSERVE_INITIAL_FIELDS];
      let fiveMomentsBenchmarkFields = [...PhpBhpSummarizedReportExcelExportComponent.OBSERVE_INITIAL_FIELDS];
      let isBenchmark = false;

      this.translateGridData(fiveMomentsGridData);

      if (this.fiveMoments.observeEvaluationFiveMomentResultList) {
        let nonBenchmarkFilters = this.fiveMoments.observeEvaluationFiveMomentResultList.filter(filter => !filter.isBenchMark);
        nonBenchmarkFilters.forEach((filter, index) => {
          fiveMomentsFields.push({ 'field': `complianceFilter${index + 1}`, 'title': `EVALUATION.COMPLIANCE`, 'group': `EVALUATION.FILTER${index + 1}` });
          fiveMomentsFields.push({ 'field': `opportunitiesFilter${index + 1}`, 'title': `DASHBOARD.OPPORTUNITIES`, 'group': `EVALUATION.FILTER${index + 1}` });
          fiveMomentsFields.push({ 'field': `positiveActionFilter${index + 1}`, 'title': `EVALUATION.POSITIVE_ACTIONS`, 'group': `EVALUATION.FILTER${index + 1}` });
        });
        let benchmarkFilters = this.fiveMoments.observeEvaluationFiveMomentResultList.filter(filter => filter.isBenchMark);
        if (benchmarkFilters.length > 0) {
          isBenchmark = true;
          benchmarkFilters.forEach((filter, index) => {
            fiveMomentsBenchmarkFields.push({ 'field': `complianceFilter${index + 1}Benchmark`, 'title': `EVALUATION.COMPLIANCE`, 'group': `EVALUATION.BENCHMARK${index + 1}` });
            fiveMomentsBenchmarkFields.push({ 'field': `opportunitiesFilter${index + 1}Benchmark`, 'title': `DASHBOARD.OPPORTUNITIES`, 'group': `EVALUATION.BENCHMARK${index + 1}` });
            fiveMomentsBenchmarkFields.push({ 'field': `positiveActionFilter${index + 1}Benchmark`, 'title': `EVALUATION.POSITIVE_ACTIONS`, 'group': `EVALUATION.BENCHMARK${index + 1}` });
          });
        }
      }

      let fiveMomentsFieldsGroupIndices = this.getGroupIndices(fiveMomentsFields);
      addedIndex = this.addGroupHeaderRows(kendoRows, fiveMomentsFieldsGroupIndices, fiveMomentsFields, addedIndex);
      addedIndex = this.addHeaderAndDataRows(kendoRows, fiveMomentsFields, fiveMomentsGridData, addedIndex);

      if (isBenchmark) {
        let fiveMomentsBenchmarkFieldsGroupIndices = this.getGroupIndices(fiveMomentsBenchmarkFields);
        addedIndex = this.addEmptyRow(kendoRows, addedIndex);
        addedIndex = this.addGroupHeaderRows(kendoRows, fiveMomentsBenchmarkFieldsGroupIndices, fiveMomentsBenchmarkFields, addedIndex);
        addedIndex = this.addHeaderAndDataRows(kendoRows, fiveMomentsBenchmarkFields, fiveMomentsGridData, addedIndex);
      }

      fiveMomentsCharts = this.getKendoCharts('fiveMoments');
      if (fiveMomentsCharts && fiveMomentsCharts.length > 0) {
        addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.DIAGRAM', addedIndex, false);
        fiveMomentsChartsStartIndex = addedIndex;
        addedIndex = this.addPlaceholderRowsForCharts(fiveMomentsCharts, addedIndex, kendoRows);
      }
    }

    if (this.asepticStatistics && this.asepticStatistics.observeEvaluationAsepticProcedureList && this.asepticStatistics.observeEvaluationAsepticProcedureList.length > 0 && asepticStatisticsFilterData.length > 0) {
      addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.ASEPTIC_STATISTICS', addedIndex, true);
      addedIndex = await this.addFiltersDataRows(kendoRows, asepticStatisticsFilterData, addedIndex,);

      let asepticStatisticsGridData = this.asepticStatistics.observationAsepticProcedureTables;
      let asepticStatisticsFields = [...PhpBhpSummarizedReportExcelExportComponent.OBSERVE_INITIAL_FIELDS];
      let asepticStatisticsBenchmarkFields = [...PhpBhpSummarizedReportExcelExportComponent.OBSERVE_INITIAL_FIELDS];
      let isBenchmark = false;

      this.translateGridData(asepticStatisticsGridData);

      if (this.asepticStatistics.observeEvaluationAsepticProcedureList) {
        let nonBenchmarkFilters = this.asepticStatistics.observeEvaluationAsepticProcedureList.filter(filter => !filter.isBenchMark);
        nonBenchmarkFilters.forEach((filter, index) => {
          asepticStatisticsFields.push({ 'field': `complianceFilter${index + 1}`, 'title': `EVALUATION.COMPLIANCE`, 'group': `EVALUATION.FILTER${index + 1}` });
          asepticStatisticsFields.push({ 'field': `opportunitiesFilter${index + 1}`, 'title': `DASHBOARD.OPPORTUNITIES`, 'group': `EVALUATION.FILTER${index + 1}` });
          asepticStatisticsFields.push({ 'field': `positiveActionFilter${index + 1}`, 'title': `EVALUATION.POSITIVE_ACTIONS`, 'group': `EVALUATION.FILTER${index + 1}` });
        });
        let benchmarkFilters = this.asepticStatistics.observeEvaluationAsepticProcedureList.filter(filter => filter.isBenchMark);
        if (benchmarkFilters.length > 0) {
          isBenchmark = true;
          benchmarkFilters.forEach((filter, index) => {
            asepticStatisticsBenchmarkFields.push({ 'field': `complianceFilter${index + 1}Benchmark`, 'title': `EVALUATION.COMPLIANCE`, 'group': `EVALUATION.BENCHMARK${index + 1}` });
            asepticStatisticsBenchmarkFields.push({ 'field': `opportunitiesFilter${index + 1}Benchmark`, 'title': `DASHBOARD.OPPORTUNITIES`, 'group': `EVALUATION.BENCHMARK${index + 1}` });
            asepticStatisticsBenchmarkFields.push({ 'field': `positiveActionFilter${index + 1}Benchmark`, 'title': `EVALUATION.POSITIVE_ACTIONS`, 'group': `EVALUATION.BENCHMARK${index + 1}` });
          });
        }
      }

      let asepticStatisticsFieldsGroupIndices = this.getGroupIndices(asepticStatisticsFields);
      addedIndex = this.addGroupHeaderRows(kendoRows, asepticStatisticsFieldsGroupIndices, asepticStatisticsFields, addedIndex);
      addedIndex = this.addHeaderAndDataRows(kendoRows, asepticStatisticsFields, asepticStatisticsGridData, addedIndex);

      if (isBenchmark) {
        let asepticStatisticsBenchmarkFieldsGroupIndices = this.getGroupIndices(asepticStatisticsBenchmarkFields);
        addedIndex = this.addEmptyRow(kendoRows, addedIndex);
        addedIndex = this.addGroupHeaderRows(kendoRows, asepticStatisticsBenchmarkFieldsGroupIndices, asepticStatisticsBenchmarkFields, addedIndex);
        addedIndex = this.addHeaderAndDataRows(kendoRows, asepticStatisticsBenchmarkFields, asepticStatisticsGridData, addedIndex);
      }

      asepticStatisticsCharts = this.getKendoCharts('asepticStatistics');
      if (asepticStatisticsCharts && asepticStatisticsCharts.length > 0) {
        addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.DIAGRAM', addedIndex, false);
        asepticStatisticsChartsStartIndex = addedIndex;
        addedIndex = this.addPlaceholderRowsForCharts(asepticStatisticsCharts, addedIndex, kendoRows);
      }
    }

    if (this.observeComments && this.observeComments.observationPredefinedCommentsResultList && this.observeComments.observationPredefinedCommentsResultList.length > 0 && observeCommentsFilters.length > 0) {
      addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.OBSERVE_COMMENTS', addedIndex, true);
      addedIndex = await this.addFiltersDataRows(kendoRows, observeCommentsFilterData, addedIndex,);

      let predefinedCommentsGridData = []
      let predefinedCommentsFields = [...PhpBhpSummarizedReportExcelExportComponent.TITLE_INITIAL_FIELD];
      let predefinedCommentsBenchmarkFields = [...PhpBhpSummarizedReportExcelExportComponent.TITLE_INITIAL_FIELD];
      let isBenchmark = false;

      for (let i = 0; i < this.observeComments.observationPredefinedCommentsResultList.length; i++) {
        const predefinedCommentsList = this.observeComments.observationPredefinedCommentsResultList[i].observationPredefinedComments;
        for (let j = 0; j < predefinedCommentsList.length; j++) {
          const predefinedComment = predefinedCommentsList[j];

          if (predefinedComment.predefinedCommentID == 1) {
            this.observeComments.observationPredefinedCommentsResultList[i].observationPredefinedComments[j].commentName = this.layoutService.translateText('SOP_OBSERVATION.JEWELLERY_OR_WRIST_WATCH');
          }
          if (predefinedComment.predefinedCommentID == 2) {
            this.observeComments.observationPredefinedCommentsResultList[i].observationPredefinedComments[j].commentName = this.layoutService.translateText('SOP_OBSERVATION.ARTIFICIAL_NAILS_OR_VARNISH');
          }
          if (predefinedComment.predefinedCommentID == 3) {
            this.observeComments.observationPredefinedCommentsResultList[i].observationPredefinedComments[j].commentName = this.layoutService.translateText('SOP_OBSERVATION.HAND_DISINFECTION_WRONG_EXECUTED');
          }
          if (predefinedComment.predefinedCommentID == 4) {
            this.observeComments.observationPredefinedCommentsResultList[i].observationPredefinedComments[j].commentName = this.layoutService.translateText('SOP_OBSERVATION.SKIN_ANTISEPSIS_WRONG_EXECUTED');
          }
          if (predefinedComment.predefinedCommentID == 5) {
            this.observeComments.observationPredefinedCommentsResultList[i].observationPredefinedComments[j].commentName = this.layoutService.translateText('SOP_OBSERVATION.PROCESS_INTERRUPTED');
          }
        }
      }

      predefinedCommentsGridData = this.observeComments.observationPredefinedCommentsResultList[0].observationPredefinedComments;
      predefinedCommentsGridData.forEach((value, index) => {
        if (predefinedCommentsGridData[index].commentName)
          predefinedCommentsGridData[index].commentName = this.layoutService.translateText(value.commentName);
      });

      let nonBenchmarkFilters = this.observeComments.observationPredefinedCommentsResultList.filter(filter => !filter.isBenchMark);
      nonBenchmarkFilters.forEach((filter, index) => {
        let res = filter.observationPredefinedComments;
        predefinedCommentsFields.push(
          { 'field': `totalTouchedComments${index + 1}`, 'title': this.layoutService.translateText('EVALUATION.TOTAL.NUMBER_TOUCHED_COMMENTS'), 'group': `EVALUATION.FILTER${index + 1}` },
          { 'field': `totalObservations${index + 1}`, 'title': this.layoutService.translateText('EVALUATION.TOTAL.AMOUNT_OBSERVATION'), 'group': `EVALUATION.FILTER${index + 1}` },
          { 'field': `predefinedCommentsPercentage${index + 1}`, 'title': this.layoutService.translateText('EVALUATION.TOUCHED_COMMENTS_PERCENT'), 'group': `EVALUATION.FILTER${index + 1}` },
        );
        for (let i = 0; i < res.length; i++) {
          if (predefinedCommentsGridData[i]) {
            predefinedCommentsGridData[i]['title'] = this.layoutService.translateText(res[i]['commentName']);
            predefinedCommentsGridData[i][`totalTouchedComments${index + 1}`] = res[i]['totalTouchedComments'];
            predefinedCommentsGridData[i][`totalObservations${index + 1}`] = res[i]['totalObservations'];
            predefinedCommentsGridData[i][`predefinedCommentsPercentage${index + 1}`] = res[i]['predefinedCommentsPercentage'];
          }
        }
      });

      let benchmarkFilters = this.observeComments.observationPredefinedCommentsResultList.filter(filter => filter.isBenchMark);
      if (benchmarkFilters.length > 0) {
        isBenchmark = true;
        benchmarkFilters.forEach((filter, index) => {
          let res = filter.observationPredefinedComments;
          predefinedCommentsBenchmarkFields.push(
            { 'field': `totalTouchedCommentsBenchmark${index + 1}`, 'title': this.layoutService.translateText('EVALUATION.TOTAL.NUMBER_TOUCHED_COMMENTS'), 'group': `EVALUATION.BENCHMARK${index + 1}` },
            { 'field': `totalObservationsBenchmark${index + 1}`, 'title': this.layoutService.translateText('EVALUATION.TOTAL.AMOUNT_OBSERVATION'), 'group': `EVALUATION.BENCHMARK${index + 1}` },
            { 'field': `predefinedCommentsPercentageBenchmark${index + 1}`, 'title': this.layoutService.translateText('EVALUATION.TOUCHED_COMMENTS_PERCENT'), 'group': `EVALUATION.BENCHMARK${index + 1}` },
          );
          for (let i = 0; i < res.length; i++) {
            if (predefinedCommentsGridData[i]) {
              predefinedCommentsGridData[i][`totalTouchedCommentsBenchmark${index + 1}`] = res[i]['totalTouchedComments'];
              predefinedCommentsGridData[i][`totalObservationsBenchmark${index + 1}`] = res[i]['totalObservations'];
              predefinedCommentsGridData[i][`predefinedCommentsPercentageBenchmark${index + 1}`] = res[i]['predefinedCommentsPercentage'];
            }
          }
        });
      }

      let predefinedCommentsFieldsGroupIndices = this.getGroupIndices(predefinedCommentsFields);
      addedIndex = this.addGroupHeaderRows(kendoRows, predefinedCommentsFieldsGroupIndices, predefinedCommentsFields, addedIndex);
      addedIndex = this.addHeaderAndDataRows(kendoRows, predefinedCommentsFields, predefinedCommentsGridData, addedIndex);

      if (isBenchmark) {
        let predefinedCommentsBenchmarkFieldsGroupIndices = this.getGroupIndices(predefinedCommentsBenchmarkFields);
        addedIndex = this.addEmptyRow(kendoRows, addedIndex);
        addedIndex = this.addGroupHeaderRows(kendoRows, predefinedCommentsBenchmarkFieldsGroupIndices, predefinedCommentsBenchmarkFields, addedIndex);
        addedIndex = this.addHeaderAndDataRows(kendoRows, predefinedCommentsBenchmarkFields, predefinedCommentsGridData, addedIndex);
      }

      observeCommentsCharts = this.getKendoCharts('observeComments');
      if (observeCommentsCharts && observeCommentsCharts.length > 0) {
        addedIndex = this.addHeaderRow(kendoRows, 'EVALUATION.DIAGRAM', addedIndex, false);
        observeCommentsChartsStartIndex = addedIndex;
        addedIndex = this.addPlaceholderRowsForCharts(observeCommentsCharts, addedIndex, kendoRows);
      }
    }

    const { worksheet, workbook } = this.convertKendoExcelToExcelJS(kendoRows);

    this.setColumnsWidth(worksheet);

    await this.setExcelHeader(worksheet, workbook);
    await Promise.all([
      this.exportCharts(processComplianceChartsStartIndex, processComplianceCharts, workbook, worksheet),
      this.exportCharts(frequencyDistributionChartsStartIndex, frequencyDistributionCharts, workbook, worksheet),
      this.exportCharts(subStepProcessComplianceChartsStartIndex, subStepProcessComplianceCharts, workbook, worksheet),
      this.exportCharts(predefinedCommentsChartsStartIndex, predefinedCommentsCharts, workbook, worksheet),
      this.exportCharts(overallComplianceChartsStartIndex, overallComplianceCharts, workbook, worksheet),
      this.exportCharts(fiveMomentsChartsStartIndex, fiveMomentsCharts, workbook, worksheet),
      this.exportCharts(asepticStatisticsChartsStartIndex, asepticStatisticsCharts, workbook, worksheet),
      this.exportCharts(observeCommentsChartsStartIndex, observeCommentsCharts, workbook, worksheet)
    ]);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    fileSaverSaveAs(blob, this.generateExcelFilename());
    this.layoutService.showLoader = false;
  }

  private translateGridData(gridData: any) {
    gridData.forEach((value, index) => {
      if (gridData[index].title)
        gridData[index].title = this.layoutService.translateText(value.title);
      if (gridData[index].name)
        gridData[index].name = this.layoutService.translateText(value.name);
    });
  }

  private addPlaceholderRowsForCharts(kendoCharts: any, addedIndex: number, kendoRows: any): number {
    const rowsToAdd = 42 * kendoCharts.length;
    for (let index = 0; index < rowsToAdd; index++) {
      addedIndex = this.addEmptyRow(kendoRows, addedIndex);
    }
    return addedIndex;
  }

  private addEmptyRow(kendoRows, addedIndex) {
    kendoRows.splice(addedIndex++, 0, {
      cells: [{}],
      type: "custom"
    });
    return addedIndex;
  }

  private processFilterData(filters, responseData) {
    return filters.map(filter => ({
      ...filter,
      countryName: responseData.countries?.find(country => country.countryID === filter.countryID)?.countryName || this.layoutService.translateText('EVALUATION.All.COUNTRIES'),
      institutionName: responseData.institutions?.find(institution => institution.institutionID === parseInt(filter.instutionIDs))?.institutionName || this.layoutService.translateText('EVALUATION.All.INSTITUTIONS'),
      wardName: responseData.wards?.find(ward => ward.wardID === parseInt(filter.wardID))?.wardName || this.layoutService.translateText('EVALUATION.All.WARDS'),
      departmentName: responseData.departments?.find(department => department.departmentID === parseInt(filter.departmentID))?.departmentName || this.layoutService.translateText('EVALUATION.All.DEPARTMENTS'),
      categoryName: responseData.professionalCategories?.find(category => category.categoryID === parseInt(filter.professionalCategoryID))?.categoryName || this.layoutService.translateText('EVALUATION.All.PROFESSIONAL_CATEGORIES'),
      subCategoryName: responseData.professionalSubCategories?.find(subCategory => subCategory.subCategoryID === parseInt(filter.subprofessionalCategoryID))?.subCategoryName || this.layoutService.translateText('EVALUATION.All.PROFESSIONAL_SUB_CATEGORIES')
    }));
  }

  private processSopData(sopIds, responseData) {
    return sopIds.map(sopId => {
      let foundSop = responseData.soPs.find(sop => sop.sopid === sopId);
      let sopTitle = foundSop ? this.layoutService.translateText(foundSop.sopTitle) : 'EVALUATION.All.SOPS';
      return { sopId, sopTitle };
    });
  }

  private addHeaderRow(kendoRows, titleKey, addedIndex, blueBorderTop) {
    const headerValue = this.layoutService.translateText(titleKey);
    kendoRows.splice(addedIndex++, 0, {
      cells: [{ value: headerValue, colSpan: 7, bold: true, fontSize: 16, borderBottom: { color: 'black', size: 1 }, borderTop: blueBorderTop ? { color: 'blue', size: 10 } : { color: 'black', size: 1 } }],
      type: "custom",
      height: 50
    });
    return addedIndex;
  }

  private addSopDataRows(kendoRows, sopData, addedIndex) {
    if (sopData.length > 0) {
      let sopString = sopData.map(sop => sop.sopTitle).join(", ");
      kendoRows.splice(addedIndex++, 0, {
        cells: [{ value: 'SOP', colSpan: 1, bold: true }, { value: sopString, colSpan: 6, wrap: true, textAlign: 'left' }],
        height: 15 + Math.ceil(sopData.length / 7) * 15,
        type: "custom"
      });
    } else {
      kendoRows.splice(addedIndex++, 0, {
        cells: [{ value: 'SOP', colSpan: 1, bold: true }, { value: this.layoutService.translateText('EVALUATION.All.SOPS'), colSpan: 6 }],
        type: "custom"
      });
    }
    return addedIndex;
  }

  private async addFiltersDataRows(kendoRows, filterData, addedIndex) {
    let tempRow = await this.addFilters(addedIndex, kendoRows, filterData);
    addedIndex = tempRow[0];
    kendoRows.splice(addedIndex++, 0, {
      cells: [{ value: this.layoutService.translateText('EVALUATION.DATA'), colSpan: 7, bold: true, fontSize: 16, borderTop: { color: 'black', size: 1 }, borderBottom: { color: 'black', size: 1 } }],
      type: "custom",
      height: 50
    });
    return addedIndex;
  }

  private getGroupIndices(fields) {
    let groupIndices = {};
    fields.forEach((field, index) => {
      if (field.group) {
        if (!groupIndices[field.group]) {
          groupIndices[field.group] = { start: index, end: index };
        } else {
          groupIndices[field.group].end = index;
        }
      }
    });
    return groupIndices;
  }

  private addGroupHeaderRows(kendoRows, fieldGroupIndices, fields, addedIndex) {
    if (Object.keys(fieldGroupIndices).length > 0) {
      let groupHeaderRowForFields = { cells: [{}], type: "group-header" };

      Object.keys(fieldGroupIndices).forEach(group => {
        let groupRange = fieldGroupIndices[group];
        groupHeaderRowForFields.cells.push({
          value: this.layoutService.translateText(group),
          colSpan: groupRange.end - groupRange.start + 1,
          bold: true,
          textAlign: "center"
        });
      });

      if (groupHeaderRowForFields.cells.length < fields.length) {
        let remainingCells = fields.length - groupHeaderRowForFields.cells.length;
        for (let i = 0; i < remainingCells; i++) {
          groupHeaderRowForFields.cells.push({ value: '', colSpan: 1 });
        }
      }

      kendoRows.splice(addedIndex++, 0, groupHeaderRowForFields);
      addedIndex;
    }
    return addedIndex;
  }

  private addHeaderAndDataRows(kendoRows, fields, gridData, addedIndex) {
    const headerCells = fields.map(field => ({
      value: this.layoutService.translateText(field.title),
      colSpan: 1,
      bold: true
    }));
    kendoRows.splice(addedIndex++, 0, { cells: headerCells, type: "header" });

    gridData.forEach(dataItem => {
      const dataRow = fields.map(field => ({ value: dataItem[field.field] }));
      kendoRows.splice(addedIndex++, 0, { cells: dataRow, type: "data" });
    });

    return addedIndex;
  }

  private convertKendoExcelToExcelJS(kendoRows: any) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(this.layoutService.translateText(this.reportName));

    let rowIndexExcelJS = 1;
    kendoRows.forEach((kRow, index) => {
      const newRow = worksheet.getRow(rowIndexExcelJS);
      newRow.height = kRow.height || 15;

      let cellIndexExcelJS = 1;
      kRow.cells.forEach((kCell) => {
        const newCell = newRow.getCell(cellIndexExcelJS);
        newCell.value = kCell.value;

        if (kRow.type === "header") {
          newCell.font = {
            color: { argb: "FF000000" },
            size: kCell.fontSize || 12,
            bold: true
          };
        } else {
          newCell.font = {
            color: { argb: "FF000000" },
            size: kCell.fontSize || 12,
            bold: kCell.bold || false
          };
        }

        newCell.alignment = {
          vertical: 'middle',
          horizontal: kCell.textAlign || 'left',
          wrapText: kCell.wrap || false
        };

        let borders = { top: {}, bottom: {}, left: {}, right: {} };

        if (kCell.borderTop && kCell.borderTop.size === 10) {
          borders.top = { style: 'thick', color: { argb: 'FF001689' } };
        } else if (kCell.borderTop) {
          borders.top = { style: 'thin', color: { argb: 'FF000000' } };
        }

        if (kCell.borderBottom) {
          borders.bottom = { style: 'thin', color: { argb: 'FF000000' } };
        }

        if (kCell.borderLeft) {
          borders.left = { style: 'thin', color: { argb: 'FF000000' } };
        }

        if (kCell.borderRight) {
          borders.right = { style: 'thin', color: { argb: 'FF000000' } };
        }

        if (Object.keys(borders).length > 0) {
          newCell.border = borders;
        }

        if (kCell.colSpan && kCell.colSpan > 1) {
          worksheet.mergeCells(rowIndexExcelJS, cellIndexExcelJS, rowIndexExcelJS, cellIndexExcelJS + kCell.colSpan - 1);
          cellIndexExcelJS += kCell.colSpan;
        } else {
          cellIndexExcelJS++;
        }
      });

      rowIndexExcelJS++;
    });
    return { worksheet, workbook };
  }

  private async setExcelHeader(worksheet: ExcelJS.Worksheet, workbook: ExcelJS.Workbook) {
    const headerStyle = {
      font: {
        size: 14,
        bold: true,
        color: { argb: 'FF001689' }
      },
      alignment: {
        horizontal: 'center',
        vertical: 'middle'
      } as ExcelJS.Alignment,
      border: {
        bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } }
      },
    } as ExcelJS.Style;

    const headerRow = worksheet.getRow(1);
    headerRow.height = 65;

    const logoBase64 = await this.layoutService.convertSvgToPngBase64('assets/img/app-logo.svg');

    const logo = workbook.addImage({
      base64: logoBase64,
      extension: 'png',
    });
    worksheet.addImage(logo, {
      tl: { col: 0.2, row: 0.2 },
      ext: { width: 135, height: 62 },
      editAs: 'absolute'
    });

    const headerCell = headerRow.getCell(1);
    headerCell.value = this.headerTitle;
    headerCell.style = headerStyle;
    worksheet.mergeCells('A1:G1');

    worksheet.views = [
      { state: 'frozen', ySplit: 3 }
    ];
  }

  private setColumnsWidth(worksheet: ExcelJS.Worksheet) {
    const MAX_COLUMN_WIDTH = 100;
    const MIN_COLUMN_WIDTH = 25;

    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        if (cell.isMerged)
          return;
        let textLength = (cell.value && cell.value.toString().length) || 0;
        if (textLength > maxLength) {
          maxLength = textLength;
        }
      });

      column.width = Math.max(Math.min(maxLength + 3, MAX_COLUMN_WIDTH), MIN_COLUMN_WIDTH);
    });
  }

  private getKendoCharts(chartType: string) {
    const chartMappings = {
      'processCompliance': {
        singleChart: 'processComplianceChart',
        chartList: 'chartListProcess'
      },
      'frequencyDistribution': {
        singleChart: 'frequencyChart',
        chartList: 'chartListFrequency'
      },
      'subStepProcessCompliance': {
        singleChart: 'subStepProcessChart',
        chartList: 'chartListSubstep'
      },
      'predefinedComments': {
        singleChart: 'predefinedCommentsChart',
        chartList: 'chartListComments'
      },
      'overallCompliance': {
        singleChart: 'overallCompliance',
        chartList: 'chartListOverallCompliance'
      },
      'fiveMoments': {
        singleChart: 'fiveMoments',
        chartList: 'chartListFiveMoments'
      },
      'asepticStatistics': {
        singleChart: 'asepticProcedures',
        chartList: 'chartListAsepticProcedures'
      },
      'observeComments': {
        singleChart: 'observeComments',
        chartList: 'chartListObserveComments'
      }
    };

    let kendoCharts = [];
    if (this.exportComponent && chartMappings[chartType]) {
      const mapping = chartMappings[chartType];
      if (this.exportComponent[mapping.singleChart]) {
        kendoCharts.push(this.exportComponent[mapping.singleChart]);
      }
      if (this.exportComponent[mapping.chartList]) {
        kendoCharts = kendoCharts.concat(this.exportComponent[mapping.chartList].toArray());
      }
    }

    return kendoCharts;
  }

  private async exportCharts(startIndex: number, kendoCharts: any[], workbook: ExcelJS.Workbook, worksheet: ExcelJS.Worksheet) {
    if (startIndex === 0 || !kendoCharts || kendoCharts.length === 0)
      return;
    for (const chart of kendoCharts) {
      let base64Image = await chart.exportImage({ width: 670, height: 830 })
        .then((dataURI) => {
          return dataURI.split(',')[1];
        });

      const chartImage = workbook.addImage({
        base64: 'data:image/png;base64,' + base64Image,
        extension: 'png',
      });

      let startRow = startIndex + (42 * kendoCharts.indexOf(chart));
      if (kendoCharts.indexOf(chart) === 0) {
        startRow += 0.1;
      }

      worksheet.addImage(chartImage, {
        tl: { col: 0, row: startRow },
        ext: { width: 670, height: 830 },
        editAs: 'absolute'
      });
    }
  }

  async addFilters(addedIndex, rows, filters) {
    let fieldNames = [
      this.layoutService.translateText('EVALUATION.FILTER'),
      this.layoutService.translateText('GENERAL.TIMEFRAME'),
      this.layoutService.translateText('CONFIGURE.COUNTRY'),
      this.layoutService.translateText('EVALUATION.GROUP_OR_INSTITUTION'),
      this.layoutService.translateText(this.translationKeys.Configure.Department),
      this.layoutService.translateText(this.translationKeys.Configure.Ward),
      this.layoutService.translateText(this.translationKeys.Configure.ProfessionalCategory),
      this.layoutService.translateText(this.translationKeys.Configure.ProfessionalSubCategory)
    ];

    if (this.userData.roleName.roleId !== 1) {
      fieldNames = fieldNames.filter(fieldName => fieldName !== this.layoutService.translateText('CONFIGURE.COUNTRY'));
    }

    if (!this.showProfessionalSubCategory) {
      fieldNames = fieldNames.filter(fieldName => fieldName !== this.layoutService.translateText(this.translationKeys.Configure.ProfessionalSubCategory));
    }

    let borderTop = { color: 'black', size: 1 };
    let borderBottom = { color: 'black', size: 1 };

    for (const fieldName of fieldNames) {
      const cells = [{
        value: fieldName,
        colSpan: 1,
        bold: true,
        borderTop: borderTop,
        borderBottom: borderBottom
      }];

      if (fieldName === this.layoutService.translateText('EVALUATION.FILTER')) {
        filters.forEach((filter, filterIndex) => {
          cells.push({
            value: filter.filterName || this.layoutService.translateText(`EVALUATION.FILTER${filterIndex + 1}`),
            colSpan: 1,
            bold: true,
            borderTop: borderTop,
            borderBottom: borderBottom
          });
        });
      } else {
        const values = await Promise.all(filters.map(filter => this.getFilterValue(filter, fieldName)));
        values.forEach(value => {
          cells.push({ value, colSpan: 1, bold: false, borderTop: borderTop, borderBottom: borderBottom });
        });
      }

      while (cells.length <= 6) {
        cells.push({ value: '', colSpan: 1, bold: false, borderTop: borderTop, borderBottom: borderBottom });
      }

      rows.splice(addedIndex++, 0, { cells, type: "custom" });
    }

    return [addedIndex, rows];
  }

  async getFilterValue(filter, fieldName) {
    switch (fieldName) {
      case this.layoutService.translateText('GENERAL.TIMEFRAME'):
        return `${new Date(filter.startDate).toLocaleDateString('en-GB')} to ${new Date(filter.endDate).toLocaleDateString('en-GB')}`;
      case this.layoutService.translateText('CONFIGURE.COUNTRY'):
        return filter.countryName || this.layoutService.translateText('EVALUATION.All.COUNTRIES');
      case this.layoutService.translateText('EVALUATION.GROUP_OR_INSTITUTION'):
        return filter.institutionName || this.layoutService.translateText('EVALUATION.All.INSTITUTIONS');
      case this.layoutService.translateText(this.translationKeys.Configure.Department):
        return this.layoutService.translateText(filter.departmentName) || this.layoutService.translateText('EVALUATION.All.DEPARTMENTS');
      case this.layoutService.translateText(this.translationKeys.Configure.Ward):
        return filter.wardName || this.layoutService.translateText('EVALUATION.All.WARDS');
      case this.layoutService.translateText(this.translationKeys.Configure.ProfessionalCategory):
        return this.layoutService.translateText(filter.categoryName || 'EVALUATION.All.PROFESSIONAL_CATEGORIES');
      case this.layoutService.translateText(this.translationKeys.Configure.ProfessionalSubCategory):
        if (filter.instutionIDs) {
          const res = await this.evaluationService.getConfigDetails(filter.instutionIDs).toPromise();
          return res.enableProfessionalSubCategory
            ? this.layoutService.translateText(filter.subCategoryName || 'EVALUATION.All.PROFESSIONAL_SUB_CATEGORIES')
            : '';
        }
        return this.layoutService.translateText(filter.subCategoryName || 'EVALUATION.All.PROFESSIONAL_SUB_CATEGORIES');
      default:
        return '';
    }
  }

  generateExcelFilename() {
    const currentDate = new Date();
    const month =
      currentDate.getMonth() + 1 > 9
        ? currentDate.getMonth() + 1
        : "0" + (currentDate.getMonth() + 1);
    const date =
      currentDate.getDate() > 9
        ? currentDate.getDate()
        : "0" + currentDate.getDate();
    const appName = this.reportName !== "" ? `_${this.appName}` : "";
    const suffix = ".xlsx";
    return `${currentDate.getFullYear()}.${month}.${date}_${this.layoutService.translateText(
      this.reportName
    )}${appName}${suffix}`;
  }
}
